import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Clock, PackagePlus, ArrowUpRight, Fingerprint, MapPinOff, Phone, Rocket, Send } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { TRIAGEM_TH } from '@/components/kanban/triagem/triagemTabela';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { useTriagemCriacaoPedido, type DestinoSemBiometria, type PedidoTriagemCriacao } from './useTriagemCriacaoPedido';
import { TriagemCriacaoPedidoRow } from './TriagemCriacaoPedidoRow';
import { BiometriaLinksDialog } from './BiometriaLinksDialog';
import { SemBiometriaDialog } from './SemBiometriaDialog';
import { ManterFilaDialog } from '@/components/kanban/triagem/ManterFilaDialog';
import { useAuth } from '@/hooks/useAuth';
import { bkoVeSoOsSeus, pedidoEhMeuOuLivre } from '@/lib/fila-permissions';
import { useContratoAdicional } from '@/hooks/useFuncoesAdicionais';
import { SoOsMeusToggle } from '@/components/kanban/triagem/SoOsMeusToggle';
import { supabase } from '@/integrations/supabase/client';
import { separarAparelho, useFlagsFaturamento } from '@/components/kanban/triagem/flagsFaturamento';

interface TriagemCriacaoPedidoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemCriacaoPedidoModal({ isOpen, onClose }: TriagemCriacaoPedidoModalProps) {
  const {
    pedidos: pedidosTodos,
    isLoading,
    refetch,
    invalidateAllQueries,
    enviarParaBiometria,
    moverSemBiometria,
    manterNoFim,
    liberarPedido,
    pegarNaCriacao,
  } = useTriagemCriacaoPedido();
  /* Só o pedido clicado fica ocupado; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). */
  const { ocupada, ocupar } = useLinhasOcupadas();

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const { profile } = useAuth();
  /* Cada BKO vê só os pedidos dele e os sem dono (Douglas 09/10/2026, régua em
     `lib/fila-permissions.ts`). Supervisora do Backoffice e gestão veem tudo. */
  const { adicional } = useContratoAdicional();
  const [soOsMeusAdicional, setSoOsMeusAdicional] = useState(true);
  const soOsSeus = bkoVeSoOsSeus(profile as { funcao?: string | null } | null) || (adicional && soOsMeusAdicional);
  const pedidos = useMemo(
    () => pedidosTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)
      && (!soOsSeus || pedidoEhMeuOuLivre(r.dono_id, profile?.id))),
    [pedidosTodos, segmento, soOsSeus, profile?.id],
  );

  const [pedidoBiometria, setPedidoBiometria] = useState<PedidoTriagemCriacao | null>(null);
  const [semBiometria, setSemBiometria] = useState<{ pedido: PedidoTriagemCriacao; destino: DestinoSemBiometria } | null>(null);
  const [pedidoManter, setPedidoManter] = useState<PedidoTriagemCriacao | null>(null);

  /* Adiar custa (Douglas 09/10/2026): cada Fim da fila nesta triagem desconta
     da comissão de quem apertou. O valor vem da régua do mês, a mesma que a
     medida e o Guia rápido leem — nada escrito aqui. */
  const compMes = format(new Date(), 'yyyy-MM-01');
  const regrasMes = useQuery({
    queryKey: ['regras-produtividade-mes', compMes],
    staleTime: 60_000,
    enabled: isOpen,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('fn_regras_produtividade_mes' as never, { p_competencia: compMes } as never);
      if (error) throw error;
      return (data ?? {}) as { contrato?: { desconto_por_adiamento?: number } };
    },
  });
  const custoAdiar = Number(regrasMes.data?.contrato?.desconto_por_adiamento) || 0;
  const avisoAdiar = custoAdiar > 0
    ? `Passar para o fim da fila desconta ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(custoAdiar)} da sua comissão do mês, com qualquer justificativa.`
    : null;

  /* LEI 18/08/2026 — "a vez de cada um" — CORRIGIDA EM 21/08/2026.
     Libera o PRIMEIRO da fila que seja MEU ou LIVRE. Como a fila é ordenada por
     tempo (mais antigo em cima), isso é o mesmo que dizer: o pedido mais atrasado
     que eu posso trabalhar. Pedido de OUTRO BKO continua travado — isso não mudou.

     ⛔ O QUE ESTAVA ERRADO (Douglas 21/08): a regra era "se você tem pedido seu na
     fila, SÓ o seu abre; livre é só para quem não tem nenhum". Quem já tinha um
     pedido ficava proibido de pegar um livre — e os livres eram justamente os
     ANTIGOS, de antes da lei, que nasceram sem dono porque na época dono não
     existia. Eles não eram de ninguém, ninguém podia pegar, e envelheciam sozinhos
     no topo da fila: 13 pedidos livres, 12 com SLA estourado, o mais velho parado
     há 3 DIAS E 10 HORAS enquanto a tela liberava para o BKO o 8º da lista.

     Douglas: "primeiro pedido mais antigo com sla vencido não fica livre pra
     iniciar criação. preciso que a regra do pedido mais atrasado libere criação
     primeiro na triagem no início da fila."

     Isto NÃO afrouxa a lei — reforça: continua sendo "trabalhe o mais antigo",
     e o mais antigo da fila passa na frente do mais antigo SEU. O que não pode é
     escolher pedido fácil, e isso segue impossível: só abre UM, e é o do topo. */
  const indiceLiberado = useMemo(() => {
    const meuId = profile?.id;
    if (!meuId) return -1;
    return pedidos.findIndex((p) => !p.dono_id || p.dono_id === meuId);
  }, [pedidos, profile?.id]);

  /* Só quem responde pela fila destrava pedido de outro: a supervisora do
     Backoffice e a gestão. BKO não solta o pedido do colega. */
  const podeLiberar =
    String((profile as { funcao?: string } | null)?.funcao || '').toUpperCase() === 'SUPERVISORA_BACKOFFICE' ||
    ['master', 'diretor', 'gerente'].includes(String(profile?.role || '').toLowerCase());

  const totalItens = pedidos.reduce((sum, p) => sum + p.itens.length, 0);
  // Aparelho anda SEPARADO do faturamento — tem KPI próprio e não é venda de
  // linha. Some do R$ da fila, não some da tela. Régua em `flagsFaturamento.ts`.
  // Soma pelos ITENS (e não por `p.valor_total`), que é onde mora o produto.
  const flagsFat = useFlagsFaturamento();
  const { faturamento: totalValue, qtdAparelhos } = separarAparelho(
    pedidos.flatMap((p) => p.itens), flagsFat, (i) => i.produto, (i) => i.subtotal || 0,
  );

  const handleConfirmBiometria = async (links: string[]) => {
    if (!pedidoBiometria) return;
    try {
      await ocupar(pedidoBiometria.pedido_id, () => enviarParaBiometria({ pedido: pedidoBiometria, links }));
      setPedidoBiometria(null);
    } catch {
      // erro já tratado no hook (toast); mantém o dialog aberto para nova tentativa
    }
  };

  return (
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={PackagePlus}
      titulo="Triagem Criação Pedido"
      contadores={{ pedidos: pedidos.length, itens: totalItens, valor: totalValue, aparelhos: qtdAparelhos }}
      segmento={segmento}
      onSegmento={setSegmento}
      cabecalhoExtra={adicional ? <SoOsMeusToggle soOsMeus={soOsMeusAdicional} onChange={setSoOsMeusAdicional} /> : undefined}
      sla={null}
      fifo={true}
      fifoLabel="A vez de cada um — trabalhe o MAIS ANTIGO da fila"
      legenda={[
        { key: 'analisar', Icone: ArrowUpRight, label: 'Analisar' },
        { key: 'ligar-whatsapp-inbox', Icone: Phone, label: 'Ligar / WhatsApp Inbox' },
        { key: 'pegar', Icone: Rocket, label: 'Pegar (linha livre)' },
        { key: 'biometria', Icone: Fingerprint, label: 'Biometria' },
        { key: 'criado-input', Icone: Send, label: 'Fibra/CPF criado → Input' },
        { key: 'aguardando-hp', Icone: MapPinOff, label: 'Fibra travou → Aguardando HP' },
        { key: 'fim-da-fila-justificar', Icone: Clock, label: 'Fim da fila (justificar)' },
      ]}
      isLoading={isLoading}
      onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
      vazio={
        totalItens === 0
          ? soOsSeus
            ? { titulo: 'Nenhum pedido seu nesta fila', descricao: 'Pegue o próximo em Pedido Fechado. Os pedidos dos colegas ficam com eles.' }
            : { titulo: '\n                Nenhum pedido pendente\n              ', descricao: '\n                Todos os pedidos foram processados na triagem de criação\n              ' }
          : undefined
      }
    >
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-10 bg-background border-b border-border">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className={`${TRIAGEM_TH} w-[58px]`}>Fila</th>
                  <th className={`${TRIAGEM_TH} w-[172px]`}>{rotuloSegmento(segmento)}</th>
                  <th className={`${TRIAGEM_TH}`}>Razão Social</th>
                  <th className={`${TRIAGEM_TH}`}>Consultor</th>
                  <th className={`${TRIAGEM_TH} w-[96px]`}>BKO</th>
                  <th className={`${TRIAGEM_TH} w-[92px]`}>Pedido</th>
                  <th className={`${TRIAGEM_TH}`}>Produto</th>
                  <th className={`${TRIAGEM_TH} w-[46px]`}>QTD</th>
                  <th className={`${TRIAGEM_TH} w-[108px]`}>Valor</th>
                  <th className={`${TRIAGEM_TH} w-[128px]`}>Tempo</th>
                  <th className={`${TRIAGEM_TH} w-[96px]`}>Contato</th>
                  <th className={`${TRIAGEM_TH} w-[262px]`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((pedido, index) => (
                  <TriagemCriacaoPedidoRow
                    key={pedido.pedido_id}
                    pedido={pedido}
                    posicao={index}
                    isTopo={index === indiceLiberado}
                    isMeu={!!pedido.dono_id && pedido.dono_id === profile?.id}
                    isProcessing={ocupada(pedido.pedido_id)}
                    podeLiberar={podeLiberar}
                    onEnviarBiometria={setPedidoBiometria}
                    onSemBiometria={(p, destino) => setSemBiometria({ pedido: p, destino })}
                    onManterFimFila={setPedidoManter}
                    onLiberar={(p) => { void ocupar(p.pedido_id, () => liberarPedido(p)).catch(() => undefined); }}
                    onPegar={(p) => { void ocupar(p.pedido_id, () => pegarNaCriacao(p)).catch(() => undefined); }}
                    onAfterCancel={invalidateAllQueries}
                  />
                ))}
              </tbody>
            </table>
        <ManterFilaDialog
          open={!!pedidoManter}
          onOpenChange={(open) => { if (!open) setPedidoManter(null); }}
          numeroPedido={pedidoManter?.numero_pedido || ''}
          razaoSocial={pedidoManter?.razao_social || ''}
          isLoading={!!pedidoManter && ocupada(pedidoManter.pedido_id)}
          aviso={avisoAdiar}
          onConfirm={async ({ justificativa, prd }) => {
            if (!pedidoManter) return;
            try {
              await ocupar(pedidoManter.pedido_id, () => manterNoFim({ pedido: pedidoManter, justificativa, prd }));
              setPedidoManter(null);
            } catch { /* toast no hook */ }
          }}
        />

        <BiometriaLinksDialog
          open={!!pedidoBiometria}
          onOpenChange={(open) => { if (!open) setPedidoBiometria(null); }}
          numeroPedido={pedidoBiometria?.numero_pedido || ''}
          razaoSocial={pedidoBiometria?.razao_social || ''}
          isLoading={!!pedidoBiometria && ocupada(pedidoBiometria.pedido_id)}
          onConfirm={handleConfirmBiometria}
        />

        <SemBiometriaDialog
          open={!!semBiometria}
          onOpenChange={(open) => { if (!open) setSemBiometria(null); }}
          destino={semBiometria?.destino || 'INPUT'}
          numeroPedido={semBiometria?.pedido.numero_pedido || ''}
          razaoSocial={semBiometria?.pedido.razao_social || ''}
          isLoading={!!semBiometria && ocupada(semBiometria.pedido.pedido_id)}
          onConfirm={() => {
            if (!semBiometria) return;
            void ocupar(semBiometria.pedido.pedido_id, () => moverSemBiometria(semBiometria))
              .then(() => setSemBiometria(null))
              .catch(() => undefined);
          }}
        />
    </TriagemShell>
  );
}
